import React, { useEffect, useState } from 'react'
import '../../style/detailed-view.scss'
import { countryLookup } from '../../utilities/countryLookup'
import TextField from '@mui/material/TextField'
import SvgIcon from '@mui/material/SvgIcon'
import Button from '@mui/material/Button'
import FormLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Divider from '@mui/material/Divider'

import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import PropTypes from 'prop-types'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import Checkbox from '@mui/material/Checkbox'
import withStyles from '@mui/styles/withStyles'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import Slide from '@mui/material/Slide'
import DialogTitle from '@mui/material/DialogTitle'
import DialogActions from '@mui/material/DialogActions'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Fade from '@mui/material/Fade'

import { useAuth0 } from '../../contexts/auth0-context'
import store from '../../redux/store'
import callAPI from '../../API/callSecureAPI'
import endpoints from '../../API/endpoints'
import Grid from '@mui/material/Grid'

import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import ReactCountryFlag from 'react-country-flag'
import { ReactComponent as GreenCircleIcon } from '../../assets/images/green_circle.svg'
import { ReactComponent as OrangeTriangleIcon } from '../../assets/images/orange_triangle.svg'
import { ReactComponent as RedSquareIcon } from '../../assets/images/red_square.svg'
import { ReactComponent as AddCircleIcon } from '../../assets/images/add-circle.svg'
import { ReactComponent as CloseIcon } from '../../assets/images/Close.svg'

import { useSelector } from 'react-redux'
import FormControl from '@mui/material/FormControl'

import Tooltip from '@mui/material/Tooltip'
import Stack from '@mui/material/Stack'
import { v4 as uuidv4 } from 'uuid'
import Switch from '@mui/material/Switch'
import useValueMessageDisplayName from '../../shared/hooks/useDisplayNames/variants/useValueMessageDisplayName'
import useEventLogger from '../../shared/hooks/useEventLogger'
import { useIsMarketMode } from '../../shared/hooks/useIsMarketMode'
import Linking from '../Linking'
import useInvalidateLinkingQueries from '../../shared/queries/linking/useInvalidateLinkingQueries'

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />
})

function TabPanel(props) {
    const { children, value, index, ...other } = props

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box p={2}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    )
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired,
}

export default function VMDetailedView({
    valueMessage,
    forceParentRefresh,
    closePanel,
    editing,
    setEditing,
    triggerSave,
    triggerCancel,
    triggerDelete,
    navigationStack,
}) {
    const isDarkMode = useSelector((state) => (state?.userRecord ? state?.userRecord?.isDarkMode : true))

    const { selectedMarket, isMarketMode } = useIsMarketMode()

    const [isLoaded, setIsLoaded] = useState(false)
    const { isAuthenticated, getTokenSilently } = useAuth0()
    const { logEvent, ACTIONS } = useEventLogger()

    const [forceRefresh, setForceRefresh] = useState(false)
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const [openSaveCountrySpecificModal, setOpenSaveCountrySpecificModal] = useState(false)

    const [valueMessageName, setValueMessageName] = useState('')
    const [newValueMessageName, setNewValueMessageName] = useState('')

    const [audiences, setAudiences] = useState({})
    const [newAudiences, setNewAudiences] = useState({})

    const [internalStakeholders, setInternalStakeholders] = useState({})
    const [newInternalStakeholders, setNewInternalStakeholders] = useState({})

    const [description, setDescription] = useState('')
    const [newDescription, setNewDescription] = useState('')

    const [category, setCategory] = useState('')

    const [priority, setPriority] = useState('')
    const [newPriority, setNewPriority] = useState('')

    const [status, setStatus] = useState('')
    const [newStatus, setNewStatus] = useState('')

    const [localMarkets, setLocalMarkets] = useState([])
    const [newLocalMarkets, setNewLocalMarkets] = useState([])

    const [settings, setSettings] = useState({})

    const [evidenceGaps, setEvidenceGaps] = useState([])
    const [newEvidenceGaps, setNewEvidenceGaps] = useState([])
    const [evidenceGapsToDelete, setEvidenceGapsToDelete] = useState([])

    const [isInternal, setIsInternal] = useState(false)
    const [newIsInternal, setNewIsInternal] = useState(false)

    const handleAddEvidenceGap = () => {
        setNewEvidenceGaps((prev) => [
            ...prev,
            {
                _id: uuidv4(), // Local identifier for the UI
                text: '',
                order: 0,
                isOpen: false,
                isNew: true,
                valueMessageId: valueMessage._id,
                linkedActivities: [],
                strategyId: store.getState().strategy._id,
                organizationId: store.getState().strategy.organizationId,
            },
        ])
    }

    const handleRemoveEvidenceGap = (localId) => {
        setNewEvidenceGaps((prev) => {
            const gapToRemove = prev.find((gap) => gap._id === localId)
            if (!gapToRemove) return prev

            // If it has a MongoDB _id, this is an existing statement => mark for deletion
            if (gapToRemove._id) {
                setEvidenceGapsToDelete((prevDeletes) => [...prevDeletes, gapToRemove._id])
            }

            // Filter it out from state
            return prev.filter((gap) => gap._id !== localId)
        })
    }

    const handleEvidenceGapTextChange = (e, gapId) => {
        const text = e.target.value
        setNewEvidenceGaps((prevGaps) => prevGaps.map((gap) => (gap._id === gapId ? { ...gap, text } : gap)))
    }

    const valueMessageDisplayName = useValueMessageDisplayName()
    const { invalidateLinkingQueries } = useInvalidateLinkingQueries()

    useEffect(() => {
        // When a VM updates, fetch the most up to date version and update local values
        if (forceRefresh) {
            setForceRefresh(false)
        }

        if (!isAuthenticated || !valueMessage) {
            return
        }
        getVMData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueMessage, forceRefresh, isAuthenticated])

    async function getVMData() {
        let token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
        updateState(valueMessage)

        let incomingEvidenceGaps = await callAPI(
            endpoints.getEvidenceGapsByValueMessageId.url + valueMessage._id,
            endpoints.getEvidenceGapsByValueMessageId.method,
            token
        )

        if (incomingEvidenceGaps.success) {
            setEvidenceGaps(incomingEvidenceGaps.data)
            setNewEvidenceGaps(incomingEvidenceGaps.data)
        }

        let incomingSettings = await callAPI(
            endpoints.getSettingsByOrganizationId.url + store.getState().strategy.organizationId,
            endpoints.getSettingsByOrganizationId.method,
            token
        )
        if (incomingSettings.success) {
            setSettings(incomingSettings.data)
        }

        setIsLoaded(true)
    }

    useEffect(() => {
        if (triggerSave === 0) {
            return
        }
        saveValueMessage()
    }, [triggerSave])

    useEffect(() => {
        if (triggerCancel === 0) {
            return
        }
        handleCancel()
    }, [triggerCancel])

    useEffect(() => {
        if (triggerDelete === 0) {
            return
        }
        handleDeleteModalOpen()
    }, [triggerDelete])

    function updateState(vm) {
        setPriority(vm.priority)
        setNewPriority(vm.priority)
        setValueMessageName(vm.name)
        setNewValueMessageName(vm.name)
        setDescription(vm.description)
        setNewDescription(vm.description)
        setAudiences(vm.audiences || {})
        setNewAudiences(vm.audiences || {})
        setInternalStakeholders(vm.internalStakeholders || {})
        setNewInternalStakeholders(vm.internalStakeholders || {})
        setCategory(vm.category.name)
        setStatus(vm.status)
        setNewStatus(vm.status)
        setLocalMarkets(vm.localMarkets)
        setNewLocalMarkets(vm.localMarkets)
        setIsInternal(vm.isInternal)
        setNewIsInternal(vm.isInternal)
    }

    async function deleteTheValueMessage() {
        let token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
        setOpenDeleteModal(false)

        if (store.getState().selectedMarket === 'Global') {
            await callAPI(
                endpoints.deleteValueMessage.url + valueMessage._id,
                endpoints.deleteValueMessage.method,
                token
            )
        } else {
            await callAPI(
                endpoints.removeCountryFromVM.url + valueMessage._id,
                endpoints.removeCountryFromVM.method,
                token,
                { countryToRemove: store.getState().selectedMarket }
            )
        }
        invalidateLinkingQueries()
        setForceRefresh(true)
        forceParentRefresh(true)
        closePanel()
    }

    async function saveValueMessage() {
        let token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })

        const isUpdatingGlobalMessageWhileInMarketMode = isMarketMode && !valueMessage.isCountrySpecific

        if (isUpdatingGlobalMessageWhileInMarketMode && !openSaveCountrySpecificModal) {
            setOpenSaveCountrySpecificModal(true)
            return
        }

        setEditing(!editing)
        let newValueMessage = {
            name: newValueMessageName,
            priority: newPriority,
            status: newStatus,
            order: valueMessage.order,
            strategyId: valueMessage.strategyId,
            organizationId: valueMessage.organizationId,
            categoryId: valueMessage.categoryId,
            narrativeId: valueMessage.narrativeId,
            linkedActivities: valueMessage.linkedActivities,
            linkedEvidence: valueMessage.linkedEvidence,
            keyEvidenceStatements: valueMessage.keyEvidenceStatements,
            description: newDescription,
            category,
            audiences: newAudiences,
            internalStakeholders: newInternalStakeholders,
            primaryEvidence: valueMessage.primaryEvidence,
            localMarkets: isMarketMode ? [selectedMarket] : newLocalMarkets,
            isInternal: newIsInternal,
            isCountrySpecific: isMarketMode,
        }

        if (isUpdatingGlobalMessageWhileInMarketMode) {
            await callAPI(endpoints.createValueMessage.url, endpoints.createValueMessage.method, token, newValueMessage)
            await callAPI(
                endpoints.removeCountryFromVM.url + valueMessage._id,
                endpoints.removeCountryFromVM.method,
                token,
                {
                    countryToRemove: selectedMarket,
                }
            )
            logEvent(ACTIONS.CREATE, { type: 'Value Message', from: valueMessage, to: newValueMessage })
        } else {
            await callAPI(
                endpoints.updateValueMessage.url + valueMessage._id,
                endpoints.updateValueMessage.method,
                token,
                newValueMessage
            )

            logEvent(ACTIONS.UPDATE, { type: 'Value Message', from: valueMessage, to: newValueMessage })
        }

        invalidateLinkingQueries()

        let evidenceGapsBody = {
            gaps: newEvidenceGaps.map((gap) => {
                if (gap.isNew) {
                    const { isNew, _id, ...gapBody } = gap
                    return gapBody
                }
                return gap
            }),
            deleteIds: evidenceGapsToDelete,
        }

        await callAPI(endpoints.handleEvidenceGaps.url, endpoints.handleEvidenceGaps.method, token, evidenceGapsBody)

        setEvidenceGaps(newEvidenceGaps)
        setNewEvidenceGaps(newEvidenceGaps)
        setEvidenceGapsToDelete([])

        setValueMessageName(newValueMessageName)
        setDescription(newDescription)
        setAudiences(newAudiences)
        setInternalStakeholders(newInternalStakeholders)
        setStatus(newStatus)
        setLocalMarkets(newLocalMarkets)
        setIsInternal(newIsInternal)
        setForceRefresh(true)
        forceParentRefresh(true)
    }

    function handleDeleteModalClose() {
        setOpenDeleteModal(false)
    }

    function handleDeleteModalOpen() {
        setOpenDeleteModal(true)
    }

    const handleVMCheckboxes = (event) => {
        setNewAudiences({
            ...newAudiences,
            [event.target.name]: event.target.checked,
        })
    }

    const handleVMInternalStakeholdersCheckboxes = (event) => {
        setNewInternalStakeholders({
            ...newInternalStakeholders,
            [event.target.name]: event.target.checked,
        })
    }

    const handleLocalMarkets = (event) => {
        if (event.target.checked) {
            if (!newLocalMarkets.includes(event.target.name)) {
                let temp = Array.from(newLocalMarkets)
                temp.push(event.target.name)
                setNewLocalMarkets(temp)
            }
        } else {
            if (newLocalMarkets.includes(event.target.name)) {
                let temp = Array.from(newLocalMarkets)
                let filtered = temp.filter((e) => e !== event.target.name)
                setNewLocalMarkets(filtered)
            }
        }
    }

    const WhiteOutlineCheckbox = withStyles({
        checked: {},
    })((props) => <Checkbox color="default" value={props.val} {...props} />)

    function handleCancel() {
        setEditing(!editing)
        setNewPriority(priority)
        setNewValueMessageName(valueMessageName)
        setNewAudiences(audiences)
        setNewInternalStakeholders(internalStakeholders)
        setNewDescription(description)
        setNewStatus(status)
        setNewLocalMarkets(localMarkets)
        setNewIsInternal(isInternal)
        setNewEvidenceGaps(evidenceGaps)
    }

    async function addPrimaryEvidence(evidenceId) {
        let token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
        await callAPI(
            endpoints.addPrimaryEvidence.url + valueMessage._id + '/' + evidenceId,
            endpoints.addPrimaryEvidence.method,
            token
        )
    }

    async function removePrimaryEvidence(evidenceId) {
        let token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
        await callAPI(
            endpoints.removePrimaryEvidence.url + valueMessage._id + '/' + evidenceId,
            endpoints.removePrimaryEvidence.method,
            token
        )
    }

    function flattenMarkets() {
        let keyMarkets = []
        for (let phase of store.getState().strategy.keyMarkets) {
            keyMarkets = keyMarkets.concat(phase.phaseKeyMarkets)
        }
        return keyMarkets
    }

    function addToStack() {
        let arr = Array.from(navigationStack)
        arr.push({
            path: '/narratives',
            data: valueMessage,
        })
        return arr
    }

    return (
        <Fade in={isLoaded} timeout={800}>
            <Grid container item sx={{ backgroundColor: isDarkMode ? '#363943' : 'white' }}>
                <Grid container item direction="column" xs spacing={0}>
                    <Dialog
                        open={openDeleteModal}
                        TransitionComponent={Transition}
                        keepMounted
                        onClose={handleDeleteModalClose}
                        aria-labelledby="alert-dialog-slide-title6"
                        aria-describedby="alert-dialog-slide-description6"
                    >
                        <DialogTitle id="alert-dialog-slide-title6">
                            <Typography variant="h4">
                                {store.getState().selectedMarket === 'Global'
                                    ? `Are you sure you want to delete this ${valueMessageDisplayName.singular}?`
                                    : `The ${valueMessageDisplayName.singular} will only be removed from Country View. Do you wish to continue?`}
                            </Typography>
                        </DialogTitle>
                        <DialogActions>
                            <Button onClick={handleDeleteModalClose} sx={{ mb: 1 }} variant="outlined-sm-button">
                                CANCEL
                            </Button>
                            <Button onClick={deleteTheValueMessage} sx={{ mr: 1, mb: 1 }} variant="contained-sm-button">
                                {store.getState().selectedMarket === 'Global' ? 'Delete' : 'Remove'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    {isLoaded && (
                        <Grid
                            container
                            spacing={1}
                            pb={1.5}
                            sx={{
                                borderBottomLeftRadius: '24px',
                                borderBottomRightRadius: '24px',
                            }}
                        >
                            {editing ? (
                                <Grid item container direction="column" spacing={2} p={3} m={2}>
                                    <Grid container item spacing={2}>
                                        <Grid container item direction="column" xs spacing={1}>
                                            <Grid item>
                                                <Typography variant="h5">{valueMessageDisplayName.singular}</Typography>
                                            </Grid>
                                            <Grid item>
                                                <TextField
                                                    id="edit-vm"
                                                    multiline
                                                    minRows={4}
                                                    variant="outlined"
                                                    placeholder={valueMessageName}
                                                    value={newValueMessageName}
                                                    onFocus={(event) => {
                                                        event.target.setAttribute('autocomplete', 'off')
                                                    }}
                                                    onChange={(e) => setNewValueMessageName(e.target.value)}
                                                    fullWidth
                                                    InputProps={{
                                                        style: {
                                                            borderRadius: '12px',
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container item direction="column" xs spacing={1}>
                                            <Grid item>
                                                <Typography variant="h5">Description</Typography>
                                            </Grid>
                                            <Grid item>
                                                <TextField
                                                    id="edit-vm"
                                                    multiline
                                                    variant="outlined"
                                                    minRows={4}
                                                    placeholder={description}
                                                    value={newDescription}
                                                    onFocus={(event) => {
                                                        event.target.setAttribute('autocomplete', 'off')
                                                    }}
                                                    onChange={(e) => setNewDescription(e.target.value)}
                                                    fullWidth
                                                    InputProps={{
                                                        style: {
                                                            borderRadius: '12px',
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item>
                                        <Stack direction="column" gap={2}>
                                            <Stack direction="row" gap={1} alignItems="center">
                                                <Typography variant="title2">
                                                    <strong>Evidence Gaps</strong>
                                                </Typography>
                                                <Tooltip title="Add a new Evidence Gap">
                                                    <IconButton onClick={handleAddEvidenceGap}>
                                                        <SvgIcon
                                                            component={AddCircleIcon}
                                                            color="primary"
                                                            inheritViewBox
                                                            fontSize="medium"
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                            <Stack direction="column" gap={2} pl={3}>
                                                {newEvidenceGaps.map((evidenceGap, index) => (
                                                    <Stack
                                                        key={evidenceGap._id}
                                                        direction="row"
                                                        alignItems="flex-start"
                                                        gap={1}
                                                    >
                                                        <Stack
                                                            flex={1}
                                                            direction="column"
                                                            gap={1.5}
                                                            sx={{
                                                                '&:before': {
                                                                    content: `"${index + 1}."`,
                                                                    position: 'absolute',
                                                                    left: '26px',
                                                                    color: 'figma.grayscale.tier4',
                                                                },
                                                            }}
                                                        >
                                                            <TextField
                                                                id={'evidence-gap' + evidenceGap._id}
                                                                multiline={false}
                                                                variant="outlined"
                                                                placeholder={evidenceGap.text}
                                                                value={evidenceGap.text}
                                                                onFocus={(event) => {
                                                                    event.target.setAttribute('autocomplete', 'off')
                                                                }}
                                                                onChange={(e) =>
                                                                    handleEvidenceGapTextChange(e, evidenceGap._id)
                                                                }
                                                                fullWidth
                                                                sx={{
                                                                    '.MuiInputBase-root': {
                                                                        borderRadius: '12px',
                                                                    },
                                                                }}
                                                            />
                                                        </Stack>
                                                        <Tooltip title="Remove Evidence Gap">
                                                            <IconButton
                                                                onClick={() => handleRemoveEvidenceGap(evidenceGap._id)}
                                                            >
                                                                <SvgIcon
                                                                    component={CloseIcon}
                                                                    inheritViewBox
                                                                    fontSize="medium"
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        </Stack>
                                    </Grid>
                                    <Grid container item spacing={4} alignItems="center">
                                        <Grid item xs={4}>
                                            <Typography variant="h5">Status</Typography>
                                            <FormControl fullWidth>
                                                <Select
                                                    variant="outlined"
                                                    sx={{ borderRadius: '12px', mt: 1 }}
                                                    value={newStatus}
                                                    onChange={(e) => setNewStatus(e.target.value)}
                                                >
                                                    <MenuItem value="Supported">Supported</MenuItem>
                                                    <MenuItem value="Supported-suboptimal">
                                                        Supported-suboptimal
                                                    </MenuItem>
                                                    <MenuItem value="Aspirational">Aspirational</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="h5">Priority</Typography>
                                            <FormControl fullWidth>
                                                <Select
                                                    variant="outlined"
                                                    sx={{ borderRadius: '12px', mt: 1 }}
                                                    value={newPriority}
                                                    onChange={(e) => setNewPriority(e.target.value)}
                                                >
                                                    <MenuItem value="Low">Low</MenuItem>
                                                    <MenuItem value="Medium">Medium</MenuItem>
                                                    <MenuItem value="High">High</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="h5">External/Internal</Typography>
                                            <FormControl fullWidth sx={{ mt: 1 }}>
                                                <Switch
                                                    checked={newIsInternal}
                                                    onChange={(e) => setNewIsInternal(e.target.checked)}
                                                    name="External/Internal"
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                    <Grid container item spacing={2}>
                                        <Grid container item spacing={1} direction="column">
                                            <Grid item>
                                                <Typography variant="h5">Audiences</Typography>
                                            </Grid>
                                            <Grid item>
                                                <FormGroup row>
                                                    {settings.vmAudiences.map((audience, i) => (
                                                        <FormControlLabel
                                                            key={i}
                                                            control={
                                                                <WhiteOutlineCheckbox
                                                                    checked={newAudiences[audience]}
                                                                    onChange={handleVMCheckboxes}
                                                                    name={audience}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={audience}
                                                        />
                                                    ))}
                                                </FormGroup>
                                            </Grid>
                                        </Grid>

                                        <Grid container item spacing={1} direction="column">
                                            <Grid item>
                                                <Typography variant="h5">Internal Stakeholders</Typography>
                                            </Grid>
                                            <Grid item>
                                                <FormGroup row>
                                                    {settings.vmInternalStakeholders?.map((internalStakeholder, i) => (
                                                        <FormControlLabel
                                                            key={i}
                                                            control={
                                                                <WhiteOutlineCheckbox
                                                                    checked={
                                                                        newInternalStakeholders[internalStakeholder]
                                                                    }
                                                                    onChange={handleVMInternalStakeholdersCheckboxes}
                                                                    name={internalStakeholder}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={internalStakeholder}
                                                        />
                                                    ))}
                                                </FormGroup>
                                            </Grid>
                                        </Grid>

                                        {settings &&
                                            settings.enableLocalizedMarkets &&
                                            store.getState().selectedMarket === 'Global' && (
                                                <Grid container item direction="column" xs={6} spacing={1}>
                                                    <Grid item>
                                                        <Typography variant="h5">Markets</Typography>
                                                    </Grid>
                                                    <Grid container item sx={{ pl: '0 !important' }}>
                                                        <FormGroup row>
                                                            {store.getState().strategy &&
                                                                flattenMarkets().map((market) => (
                                                                    <Grid
                                                                        container
                                                                        item
                                                                        xs="auto"
                                                                        alignItems="center"
                                                                        spacing={1}
                                                                        sx={{ pr: 4 }}
                                                                    >
                                                                        <Grid item>
                                                                            <WhiteOutlineCheckbox
                                                                                checked={newLocalMarkets.includes(
                                                                                    market.code
                                                                                )}
                                                                                onChange={handleLocalMarkets}
                                                                                name={market.code}
                                                                                color="primary"
                                                                                inputProps={{
                                                                                    'aria-label': 'controlled',
                                                                                }}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item>
                                                                            <ReactCountryFlag
                                                                                countryCode={market.code?.toLowerCase()}
                                                                                svg
                                                                                cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                                                                                cdnSuffix="svg"
                                                                                style={{
                                                                                    width: '1.7em',
                                                                                    height: '1.7em',
                                                                                    margin: '2px',
                                                                                }}
                                                                                title={market.label}
                                                                                className="countryFlag"
                                                                            />
                                                                        </Grid>

                                                                        <Grid item>
                                                                            <FormLabel>{market.label}</FormLabel>
                                                                        </Grid>
                                                                    </Grid>
                                                                ))}
                                                        </FormGroup>
                                                    </Grid>
                                                </Grid>
                                            )}
                                    </Grid>
                                </Grid>
                            ) : (
                                <Grid item container xs={12}>
                                    <Accordion
                                        defaultExpanded
                                        square
                                        sx={{
                                            width: '100%',
                                            backgroundColor: isDarkMode ? '#5E6169' : '#F5F5F6',
                                            boxShadow: 'none',
                                            p: 1,
                                        }}
                                    >
                                        <AccordionSummary
                                            sx={{
                                                backgroundColor: isDarkMode ? '#5E6169' : '#F5F5F6',
                                            }}
                                            expandIcon={
                                                <IconButton>
                                                    <ExpandMoreIcon color="primary" />
                                                </IconButton>
                                            }
                                            aria-controls="panel1a-content"
                                            id="panel1a-header"
                                        >
                                            <Grid
                                                container
                                                item
                                                direction="column"
                                                spacing={1}
                                                sx={{
                                                    backgroundColor: isDarkMode ? '#5E6169' : '#F5F5F6',
                                                    pt: 0.5,
                                                }}
                                            >
                                                <Grid item>
                                                    <Typography variant="h5">
                                                        {valueMessageDisplayName.singular}:
                                                    </Typography>
                                                </Grid>
                                                <Grid container direction="row"></Grid>
                                                <Grid container item direction="row" spacing={1}>
                                                    <Grid item xs={10}>
                                                        <div
                                                            style={{
                                                                display: 'inline-block',
                                                                position: 'relative',
                                                                zIndex: 2,
                                                                userSelect: 'text',
                                                                cursor: 'text',
                                                                padding: '16px 20px',
                                                                margin: '-16px -16px',
                                                            }}
                                                            onClick={(e) => e.stopPropagation()} // prevent collapse on text click
                                                        >
                                                            <Typography
                                                                variant="h2"
                                                                onDoubleClick={(e) => {
                                                                    const range = document.createRange()
                                                                    const selection = window.getSelection()
                                                                    range.selectNodeContents(e.target)
                                                                    selection.removeAllRanges()
                                                                    selection.addRange(range)
                                                                }}
                                                            >
                                                                {valueMessageName}
                                                            </Typography>
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs>
                                                        {status === 'Supported' && (
                                                            <Chip
                                                                icon={
                                                                    <SvgIcon
                                                                        fontSize="small"
                                                                        component={GreenCircleIcon}
                                                                        inheritViewBox
                                                                    />
                                                                }
                                                                label={
                                                                    <Typography variant="chip">Supported</Typography>
                                                                }
                                                            />
                                                        )}
                                                        {status === 'Supported-suboptimal' && (
                                                            <Chip
                                                                icon={
                                                                    <SvgIcon
                                                                        fontSize="small"
                                                                        component={OrangeTriangleIcon}
                                                                        inheritViewBox
                                                                    />
                                                                }
                                                                label={
                                                                    <Typography variant="chip">
                                                                        Supported-suboptimal
                                                                    </Typography>
                                                                }
                                                            />
                                                        )}
                                                        {status === 'Aspirational' && (
                                                            <Chip
                                                                icon={
                                                                    <SvgIcon
                                                                        fontSize="small"
                                                                        component={RedSquareIcon}
                                                                        inheritViewBox
                                                                    />
                                                                }
                                                                label={
                                                                    <Typography variant="chip">Aspirational</Typography>
                                                                }
                                                            />
                                                        )}
                                                    </Grid>
                                                    <Grid item xs>
                                                        <Chip
                                                            label={
                                                                <Typography variant="chip">
                                                                    {isInternal ? 'Internal' : 'External'}
                                                                </Typography>
                                                            }
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={3}>
                                                {
                                                    <Grid container item direction="column">
                                                        <Grid item sx={{ mt: 1 }}>
                                                            <Typography variant="h5">Description</Typography>
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            sx={{
                                                                p: 1,
                                                                mt: 1,
                                                                border: 1,
                                                                borderColor: '#CCCCCC',
                                                                borderRadius: '16px',
                                                            }}
                                                            elevation={1}
                                                        >
                                                            <Typography variant="body3">
                                                                {description && description.length > 1
                                                                    ? description
                                                                    : 'No description to display'}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                }
                                            </Grid>
                                            <Divider // Dividing line between Description and Details
                                                sx={{
                                                    borderBottom: 1,
                                                    borderColor: '#CCCCCC',
                                                    pt: 3,
                                                    mb: 5,
                                                }}
                                            ></Divider>
                                            <Grid container direction="row">
                                                <Grid
                                                    container
                                                    item
                                                    spacing={3}
                                                    xs={12}
                                                    sm={6}
                                                    direction="row"
                                                    sx={{ mt: -6 }}
                                                >
                                                    <Grid item xs="auto">
                                                        <Typography variant="h5">Details</Typography>
                                                    </Grid>
                                                    <Grid container item alignItems="center" spacing={2}>
                                                        <Grid item xs="auto">
                                                            <Typography variant="title2">Category:</Typography>
                                                        </Grid>
                                                        <Grid item xs>
                                                            <Chip
                                                                label={
                                                                    <Typography variant="chip">{category}</Typography>
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid item xs="auto">
                                                            <Typography variant="title2">Priority:</Typography>
                                                        </Grid>
                                                        <Grid item xs>
                                                            <Chip
                                                                label={
                                                                    <Typography variant="chip">
                                                                        {priority || 'No priority'}
                                                                    </Typography>
                                                                }
                                                                sx={{ ml: 1 }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                    <Grid // horizontal divider betwen Category and Audiences
                                                        item
                                                        xs={12}
                                                        sx={{
                                                            ml: 3,
                                                            mt: -1,
                                                            mb: -1,
                                                            borderBottom: 1,
                                                            borderColor: '#CCCCCC',
                                                        }}
                                                    ></Grid>
                                                    <Grid container item spacing={2} alignItems="flex-start">
                                                        <Grid item xs="auto">
                                                            <Typography variant="title2">Audiences:</Typography>
                                                        </Grid>
                                                        {Object.values(audiences).some((value) => value) ? (
                                                            <Grid
                                                                container
                                                                item
                                                                xs="auto"
                                                                direction="row"
                                                                flexWrap="wrap"
                                                                flexShrink={'1 !important'}
                                                                gap={1}
                                                            >
                                                                {Object.keys(audiences).map((audience, i) => {
                                                                    if (audiences[audience]) {
                                                                        return (
                                                                            <Grid item key={i}>
                                                                                <Chip
                                                                                    label={
                                                                                        <Typography variant="chip">
                                                                                            {audience}
                                                                                        </Typography>
                                                                                    }
                                                                                    sx={{ mr: 1 }}
                                                                                />
                                                                            </Grid>
                                                                        )
                                                                    }
                                                                    return null
                                                                })}
                                                            </Grid>
                                                        ) : (
                                                            <Grid item xs={12} sx={{ mt: -2 }}>
                                                                <Typography variant="body3">
                                                                    No audiences to display
                                                                </Typography>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                    <Grid container item spacing={2} alignItems="flex-start">
                                                        <Grid item xs="auto">
                                                            <Typography variant="title2">
                                                                Internal Stakeholders:
                                                            </Typography>
                                                        </Grid>
                                                        {Object.values(internalStakeholders).some((value) => value) ? (
                                                            <Grid
                                                                container
                                                                item
                                                                xs="auto"
                                                                direction="row"
                                                                flexWrap="wrap"
                                                                flexShrink={'1 !important'}
                                                                gap={1}
                                                            >
                                                                {Object.keys(internalStakeholders).map(
                                                                    (internalStakeholder, i) => {
                                                                        if (internalStakeholders[internalStakeholder]) {
                                                                            return (
                                                                                <Grid item key={i}>
                                                                                    <Chip
                                                                                        label={
                                                                                            <Typography variant="chip">
                                                                                                {internalStakeholder}
                                                                                            </Typography>
                                                                                        }
                                                                                        sx={{ mr: 1 }}
                                                                                    />
                                                                                </Grid>
                                                                            )
                                                                        }
                                                                        return null
                                                                    }
                                                                )}
                                                            </Grid>
                                                        ) : (
                                                            <Grid item xs={12} sx={{ mt: -2 }}>
                                                                <Typography variant="body3">
                                                                    No internal stakeholders to display
                                                                </Typography>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                    <Grid container item spacing={2} alignItems="flex-start">
                                                        <Grid item xs="auto">
                                                            <Typography variant="title2">Evidence Gaps:</Typography>
                                                        </Grid>
                                                        {!evidenceGaps?.length && (
                                                            <Grid item xs={12} sx={{ mt: -2 }}>
                                                                <Typography variant="body3">
                                                                    No evidence gaps to display
                                                                </Typography>
                                                            </Grid>
                                                        )}
                                                        {evidenceGaps?.map((value, index) => (
                                                            <Grid item xs={12} key={index}>
                                                                <li>{value.text}</li>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Grid>
                                                <Grid // horizontal divider betwen Audiences and Markets only when breakpoint is xs
                                                    item
                                                    xs={12}
                                                    sx={{
                                                        mt: 2,
                                                        mb: 4,
                                                        borderBottom: 1,
                                                        borderColor: '#CCCCCC',
                                                        display: {
                                                            xs: 'block',
                                                            sm: 'none',
                                                        },
                                                    }}
                                                ></Grid>
                                                <Grid // vertical dividing line between Markets and Linked Data
                                                    item
                                                    xs={0}
                                                    sx={{
                                                        ml: 2,
                                                        mr: 2,
                                                        mt: -3,
                                                        mb: -1,
                                                        borderLeft: 1,
                                                        borderColor: '#CCCCCC',
                                                        display: {
                                                            xs: 'none',
                                                            sm: 'block',
                                                            md: 'block',
                                                            lg: 'block',
                                                            xl: 'block',
                                                        },
                                                    }}
                                                ></Grid>
                                                <Grid
                                                    container
                                                    item
                                                    spacing={2}
                                                    xs={5}
                                                    alignItems="flex-start"
                                                    sx={{ mt: -5 }}
                                                >
                                                    {settings && settings.enableLocalizedMarkets && (
                                                        <Grid container item direction="column" xs="12">
                                                            <Grid item sx={{ mb: 2 }}>
                                                                <Typography variant="h5">Markets</Typography>
                                                            </Grid>
                                                            <Grid
                                                                container
                                                                justifyContent="center"
                                                                alignItems="center"
                                                                elevation={1}
                                                                sx={{
                                                                    backgroundColor: isDarkMode ? '#3B3F4D' : '#D3D5DB',
                                                                    borderRadius: '16px',
                                                                    width: 'max-content',
                                                                    maxWidth: '100%',
                                                                    pt: 0.5,
                                                                    pb: 0.5,
                                                                }}
                                                            >
                                                                {newLocalMarkets.map((market, i) => (
                                                                    <ReactCountryFlag
                                                                        key={i}
                                                                        countryCode={market.toLowerCase()}
                                                                        svg
                                                                        cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                                                                        cdnSuffix="svg"
                                                                        style={{
                                                                            width: '1.7em',
                                                                            height: '1.7em',
                                                                            margin: 8,
                                                                        }}
                                                                        title={countryLookup[market]}
                                                                        className="countryFlag"
                                                                    />
                                                                ))}
                                                            </Grid>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </Grid>

                <Grid container item direction="column" spacing={0}>
                    <Stack direction="row" p={3}>
                        <Linking type="valueMessage" id={valueMessage._id} previousDataStack={addToStack()} />
                    </Stack>
                </Grid>
                <Divider light />

                <Dialog
                    open={openSaveCountrySpecificModal}
                    TransitionComponent={Transition}
                    keepMounted
                    onClose={() => setOpenSaveCountrySpecificModal(false)}
                    aria-labelledby="alert-dialog-slide-title1"
                    aria-describedby="alert-dialog-slide-description1"
                >
                    <DialogTitle id="alert-dialog-slide-title1">
                        {'Editing Global ' + valueMessageDisplayName.singular}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-slide-description1">
                            {`Editing a Global ${valueMessageDisplayName.singular} will create a new ${valueMessageDisplayName.singular} specific to your selected
                            country. Changes made to this new ${valueMessageDisplayName.singular} will not affect the Global ${valueMessageDisplayName.singular} and
                            vice-versa. Do you wish to continue?`}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setOpenSaveCountrySpecificModal(false)
                                handleCancel()
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                setOpenSaveCountrySpecificModal(false)
                                saveValueMessage()
                            }}
                        >
                            {`YES, EDIT AND CREATE LOCAL ${valueMessageDisplayName.singular}`}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Grid>
        </Fade>
    )
}
